import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/auth/LoginPage';
import AlertsPage from './components/shared/AlertsPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Header from './components/Header';
import ManagerView from './components/manager/ManagerView';
import PracticePulsePage from './components/manager/PracticePulsePage';
import ConsumerInsightsPage from './components/manager/ConsumerInsightsPage';
import PracticeManagementPage from './components/manager/PracticeManagementPage';
import DirectorView from './components/director/DirectorView';
import ChecklistManagement from './components/admin/ChecklistManagement';
import InitiativesAssignmentsTab from './components/director/InitiativesAssignmentsTab';
import RoleManagement from './components/admin/RoleManagement';
import OrganizationHierarchyBuilder from './components/admin/OrganizationHierarchyBuilder';
import DataOwnershipDirectory from './components/admin/shared/DataOwnershipDirectory';
import SuperUserDashboard from './components/superuser/SuperUserDashboard';
import FavoritesPage from './components/shared/FavoritesPage';
import StrategicInitiativesList from './components/strategic/StrategicInitiativesList';
import InitiativeDetail from './components/strategic/InitiativeDetail';
import InitiativeForm from './components/strategic/InitiativeForm';
import StatusUpdateForm from './components/strategic/StatusUpdateForm';
import StrategicInitiativesGate from './components/strategic/StrategicInitiativesGate';
import AssignmentsView from './components/assignments/AssignmentsView';
import AssignmentsGate from './components/assignments/AssignmentsGate';
import EmailSignalDashboard from './components/emailSignal/EmailSignalDashboard';
import EmailSignalGate from './components/emailSignal/EmailSignalGate';
// AssignmentDB removed — assignments are always enabled via backend API
import { EmailSignalEngine } from './utils/emailSignalData';
import { Star, UserPlus, Users, Sliders, ClipboardList, ChevronLeft, ChevronRight, ShieldCheck, CalendarDays, Stethoscope, DollarSign, Building2, Gauge, Mail, Flag, Network, KeyRound, Database, Landmark, Bell, RefreshCw } from 'lucide-react';
import { ICON_PX } from './components/ui/lib/iconSizes';
import OnboardingWizard from './components/superuser/admin/OnboardingWizard';
import UserDirectory from './components/superuser/admin/UserDirectory';
import FeatureFlagsPage from './components/superuser/admin/FeatureFlagsPage';
import AuditLogPage from './components/superuser/admin/AuditLogPage';
import DemoSetupPage from './components/superuser/admin/DemoSetupPage';
import UserProfilePopup from './components/UserProfilePopup';
import { ThemeProvider } from './contexts/ThemeContext';
import { ConsumerUnitProvider } from './contexts/ConsumerUnitContext';
import { Toaster, TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from './components/ui';
import { ChatbotProvider } from './contexts/ChatbotContext';
import { AlertsProvider } from './contexts/AlertsContext';
import { EventStreamProvider } from './contexts/EventStreamContext';
import { ChatFAB } from './components/common/ChatFAB';
import { ChatDrawer } from './components/common/ChatDrawer';
import { useChatbot } from './contexts/ChatbotContext';
import { useAlerts } from './hooks/useAlerts';
import './index.css';
import './App.css';

const CHAT_PANEL_WIDTH = 400;

// Lazy-loaded chart tabs — JS chunk only downloads when the user first navigates there
const NsgAdminPage        = React.lazy(() => import('./components/admin/NsgAdminPage'));
const InitiativesTab      = React.lazy(() => import('./components/director/InitiativesTab'));
const SchedulingAccessPage = React.lazy(() => import('./components/manager/access/SchedulingAccessPage'));
const DirectorAccessPage  = React.lazy(() => import('./components/director/access/DirectorAccessPage'));
const QualityTab          = React.lazy(() => import('./components/director/QualityTab'));
const QualityRegulatoryPage = React.lazy(() => import('./components/manager/quality/QualityRegulatoryPage'));
const DirectorQualityRegulatoryPage = React.lazy(() => import('./components/director/quality/DirectorQualityRegulatoryPage'));
const DirectorConsumerDashboard = React.lazy(() => import('./components/director/DirectorConsumerDashboard'));
const FinancialTrackingPage = React.lazy(() => import('./components/finance/FinancialTrackingPage'));
const StaffingPage        = React.lazy(() => import('./components/manager/staffing/StaffingPage'));
const DirectorStaffingPage = React.lazy(() => import('./components/director/staffing/DirectorStaffingPage'));
const DirectorPracticeManagementDashboard = React.lazy(() => import('./components/director/DirectorPracticeManagementDashboard'));
const NetworkPulsePage = React.lazy(() => import('./components/director/networkPulse/NetworkPulsePage'));
const PopulationHealthPage = React.lazy(() => import('./components/manager/population-health/PopulationHealthPage'));
const DirectorPopulationHealthPage = React.lazy(() => import('./components/director/population-health/DirectorPopulationHealthPage'));

const MOBILE_BREAKPOINT = 768;
const DESKTOP_SIDEBAR_WIDTH = 240;
const COLLAPSED_SIDEBAR_WIDTH = 64;

/* Bridge: syncs ChatbotContext.isOpen up to AppShell (which sits above the provider) */
function ChatLayoutEffect({ onChange }) {
  const { isOpen } = useChatbot();
  useEffect(() => { onChange(isOpen); }, [isOpen, onChange]);
  return null;
}

function Sidebar({ currentRole, userRole, onRoleChange, isMobile, isMobileOpen, onCloseMobile, closeButtonRef, isCollapsed, onToggleCollapse, user, onSignOut, theme, onThemeChange, alertCount = 0 }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserPopup, setShowUserPopup] = React.useState(false);
  const sidebarWidth = isMobile ? DESKTOP_SIDEBAR_WIDTH : (isCollapsed ? COLLAPSED_SIDEBAR_WIDTH : DESKTOP_SIDEBAR_WIDTH);
  const assignmentsEnabled = true; // always enabled — backend serves data
  const emailSignalEnabled = EmailSignalEngine.isFeatureEnabled(currentRole);
  const isSuperUser = userRole === 'super-user';
  const isSystemAdmin = userRole === 'system-admin';
  const isDirector = userRole === 'director';

  const isActive = (path) => {
    if (location.pathname === path) return true;
    if ((path === '/initiatives' || path === '/director/initiatives') && location.pathname.startsWith('/strategic-initiatives')) return true;
    if ((path === '/practice-management' || path === '/manager/practice-management') && location.pathname.startsWith('/strategic-initiatives')) return true;
    if ((path === '/assignments' || path === '/manager/assignments' || path === '/director/assignments') && (location.pathname.startsWith('/manager/assignments') || location.pathname.startsWith('/assignments'))) return true;
    if ((path === '/email-signal' || path === '/manager/email-signal' || path === '/director/email-signal') && (location.pathname.startsWith('/manager/email-signal') || location.pathname.startsWith('/email-signal'))) return true;
    return false;
  };
  const getNavItems = (section) => {
    const items = section.items || [];
    // Filter out Assignments if feature not enabled
    return items.filter(item => {
      if ((item.path === '/assignments' || item.path === '/manager/assignments' || item.path === '/director/assignments') && !assignmentsEnabled) return false;
      if ((item.path === '/email-signal' || item.path === '/manager/email-signal' || item.path === '/director/email-signal') && !emailSignalEnabled) return false;
      return true;
    }).map(item => item.path === '/alerts' ? { ...item, badge: alertCount } : item);
  };

  const managerNavItems = [
    {
      section: 'Overview',
      items: [
        { path: '/manager/overview', icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        ), label: 'Practice Pulse' },
        { path: '/alerts', icon: <Bell size={ICON_PX.lg} />, label: 'Alerts' },
      ]
    },
    {
      section: 'Domains',
      items: [
        { path: '/manager/staffing',            icon: <Users size={ICON_PX.lg} />,        label: 'Staffing' },
        { path: '/manager/access',              icon: <CalendarDays size={ICON_PX.lg} />, label: 'Scheduling & Access' },
        { path: '/manager/population-health',   icon: <Stethoscope size={ICON_PX.lg} />, label: 'Population Health' },
        { path: '/manager/finance',             icon: <DollarSign size={ICON_PX.lg} />,   label: 'Financial Tracking' },
        { path: '/manager/consumer-insights',   icon: <Gauge size={ICON_PX.lg} />,        label: 'Consumer Insights' },
        { path: '/manager/practice-management', icon: <Building2 size={ICON_PX.lg} />,    label: 'Practice Management' },
      ]
    },
  ];

  const directorNavItems = [
    {
      section: 'Overview',
      items: [
        { path: '/director/overview', icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        ), label: 'Network Pulse' },
        { path: '/alerts', icon: <Bell size={ICON_PX.lg} />, label: 'Alerts' },
      ]
    },
    {
      section: 'Dashboard',
      items: [
        { path: '/director/hierarchy',        icon: <Network size={ICON_PX.lg} />,       label: 'Team Hierarchy' },
        { path: '/director/access',           icon: <CalendarDays size={ICON_PX.lg} />,  label: 'Access' },
        { path: '/director/quality-regulatory', icon: <ShieldCheck size={ICON_PX.lg} />,   label: 'Quality & Regulatory' },
        { path: '/director/staffing',         icon: <Users size={ICON_PX.lg} />,         label: 'Staffing' },
        { path: '/director/finance',          icon: <DollarSign size={ICON_PX.lg} />,    label: 'Finance' },
        { path: '/director/initiatives',      icon: <Flag size={ICON_PX.lg} />,          label: 'Initiatives' },
        { path: '/director/assignments',      icon: <ClipboardList size={ICON_PX.lg} />, label: 'Assignments' },
        { path: '/director/email-signal',     icon: <Mail size={ICON_PX.lg} />,          label: 'Email Signals' },
        { path: '/director/population-health',icon: <Stethoscope size={ICON_PX.lg} />,   label: 'Population Health' },
        { path: '/director/consumer-insights',icon: <Gauge size={ICON_PX.lg} />,         label: 'Consumer Insights' },
        { path: '/director/practice-management', icon: <Building2 size={ICON_PX.lg} />, label: 'Practice Management' },
        { path: '/favorites',                 icon: <Star size={ICON_PX.lg} />,          label: 'Favorites' },
      ]
    },
    {
      section: 'Admin',
      items: [
        { path: '/admin/checklists',   icon: <ClipboardList size={ICON_PX.lg} />, label: 'Checklists' },
        { path: '/admin/roles',        icon: <KeyRound size={ICON_PX.lg} />,      label: 'Role Management' },
        { path: '/admin/org',          icon: <Landmark size={ICON_PX.lg} />,      label: 'Organization' },
        { path: '/admin/data-sources', icon: <Database size={ICON_PX.lg} />,      label: 'Data Sources' },
      ]
    }
  ];

  const configSection = {
    section: 'Configuration',
    items: [
      { path: '/super-user/overview', icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ), label: 'Overview' },
      { path: '/super-user/dashboards', icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ), label: 'Dashboard Config' },
      { path: '/super-user/workflows', icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ), label: 'Workflows & Tasks' },
      { path: '/super-user/metrics', icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ), label: 'Metrics & Thresholds' },
      { path: '/super-user/alerts', icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ), label: 'Alerts & Escalations' },
      { path: '/super-user/features', icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      ), label: 'Feature Management' },
      { path: '/super-user/training', icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ), label: 'Training & Support' },
    ]
  };

  const nexoNurseAdminSection = {
    section: 'NexoNurse Admin',
    items: [
      { path: '/system-admin/onboarding', icon: <UserPlus className="w-5 h-5" />, label: 'Onboarding' },
      { path: '/system-admin/users', icon: <Users className="w-5 h-5" />, label: 'User Directory' },
      { path: '/system-admin/feature-flags', icon: <Sliders className="w-5 h-5" />, label: 'Feature Flags' },
      { path: '/system-admin/audit-log', icon: <ClipboardList className="w-5 h-5" />, label: 'Audit Log' },
      { path: '/system-admin/nsg', icon: <ShieldCheck className="w-5 h-5" />, label: 'NSG Gateway' },
      { path: '/system-admin/demo-setup', icon: <RefreshCw className="w-5 h-5" />, label: 'Demo Setup' },
    ]
  };

  // Composed nav sets: each role sees the appropriate sections
  const superUserNavItems = [directorNavItems[0], directorNavItems[1], configSection];
  const systemAdminNavItems = [directorNavItems[0], directorNavItems[1], configSection, nexoNurseAdminSection];

  const roleLabel =
    currentRole === 'system-admin' ? 'System Administrator' :
    currentRole === 'super-user' ? 'Super User' :
    currentRole === 'director' ? 'Service Line Director' : 'Unit Manager';

  const handleRoleChange = (value) => {
    onRoleChange(value);
    if (isMobile && onCloseMobile) onCloseMobile();
    const overviewPath =
      value === 'system-admin' ? '/system-admin/onboarding' :
      value === 'director' ? '/director/overview' :
      value === 'super-user' ? '/director/overview' : '/manager/overview';
    navigate(overviewPath);
  };

  return (
    <aside
      data-testid="app-sidebar"
      className={["flex flex-col transition-all duration-300 h-screen fixed left-0 top-0 z-40 sidebar-nav-wrap", isMobile ? "mobile-sidebar" : "", isMobile && isMobileOpen ? "open" : ""].join(" ")}
      style={{ width: sidebarWidth }}
      role={isMobile ? 'dialog' : 'navigation'}
      aria-modal={isMobile ? 'true' : undefined}
      aria-label="Application Sidebar"
    >
      <div className="sidebar-brand-wrap" data-testid="sidebar-brand" style={{ padding: isCollapsed ? '14px 14px 6px' : '14px 20px 6px' }}>
        {/* Row 1: Brand mark + name + close button (mobile only) */}
        <div className="flex items-center gap-3 min-w-0" style={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
          <div className="brand-mark-nn" style={{ flexShrink: 0 }}><span className="n-royal">N</span><span className="n-aqua">N</span></div>
          {!isCollapsed && (
            <div className="min-w-0 flex-1">
              <div className="font-serif text-[15px] font-bold text-text-primary tracking-[0.3px]">NexoNurse</div>
              <div className="text-[10px] text-text-muted tracking-[0.3px] mt-px">Command Center</div>
            </div>
          )}
          {isMobile && (
            <button
              ref={closeButtonRef}
              onClick={onCloseMobile}
              className="p-2 rounded-lg transition-colors"
              style={{ color: 'var(--text-secondary)', marginLeft: 'auto' }}
              aria-label="Close sidebar menu"
              title="Close sidebar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {!isCollapsed && (isSystemAdmin || isSuperUser || isDirector) && onRoleChange && (
        <>
          <div className="sidebar-role-label">{isSystemAdmin ? 'Impersonate Role' : 'View As'}</div>
          <select
            value={currentRole}
            onChange={(e) => handleRoleChange(e.target.value)}
            className="sidebar-role-select"
          >
            {isSystemAdmin && <option value="system-admin">System Admin</option>}
            {(isSystemAdmin || isSuperUser) && <option value="super-user">Super User</option>}
            <option value="director">Director/VP</option>
            <option value="manager">Unit Manager</option>
          </select>
        </>
      )}

      <nav
        className="flex-1 overflow-y-auto p-3"
        style={{ paddingTop: 18, paddingLeft: 14, paddingRight: 14 }}
        onClick={(event) => {
          if (!isMobile || !onCloseMobile) return;
          if (event.target.closest('a')) onCloseMobile();
        }}
      >
        {(() => {
          const baseNavItems =
            currentRole === 'system-admin' ? systemAdminNavItems :
            currentRole === 'super-user' ? superUserNavItems :
            currentRole === 'director' ? directorNavItems :
            managerNavItems;
          // system-admin impersonating another role: append NexoNurse Admin
          const navItems = (isSystemAdmin && currentRole !== 'system-admin')
            ? [...baseNavItems, nexoNurseAdminSection]
            : baseNavItems;
          return navItems.map((section, idx) => (
            <div key={idx} className="mb-6">
              {!isCollapsed && (
                <div className="sidebar-role-label" style={{ padding: '0 8px', marginBottom: 6 }}>{section.section}</div>
              )}
              <ul className="list-none space-y-0.5">
                {getNavItems(section).map((item) => (
                  <li key={item.path}>
                    {isCollapsed ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link
                            to={item.path}
                            className={`sidebar-nav-link ${isActive(item.path) ? 'active' : ''}`}
                          >
                            <span className="nav-icon-svg" style={{ color: 'inherit' }}>{item.icon}</span>
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="right">{item.label}</TooltipContent>
                      </Tooltip>
                    ) : (
                      <Link
                        to={item.path}
                        className={`sidebar-nav-link ${isActive(item.path) ? 'active' : ''}`}
                      >
                        <span className="nav-icon-svg" style={{ color: 'inherit' }}>{item.icon}</span>
                        <span className="flex-1 text-[13px]">{item.label}</span>
                        {item.badge > 0 && (
                          <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-critical text-white">{item.badge ?? 0}</span>
                        )}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ));
        })()}
      </nav>

      <div className="sidebar-user-wrap" style={{ position: 'relative' }}>
        {showUserPopup && (
          <UserProfilePopup
            user={user}
            roleLabel={roleLabel}
            theme={theme}
            onThemeChange={onThemeChange}
            onSignOut={onSignOut}
            onClose={() => setShowUserPopup(false)}
          />
        )}
        <div className="flex items-center gap-3">
          <button
            data-testid="user-avatar-btn"
            onClick={() => setShowUserPopup(p => !p)}
            className="user-avatar"
            style={{ width: 34, height: 34, fontSize: 12, cursor: 'pointer', border: 'none', flexShrink: 0 }}
            title="Profile & settings"
            aria-label="Open profile menu"
          >
            {user ? (user.attributes?.name || user.attributes?.email || '?').charAt(0).toUpperCase() : '?'}
          </button>
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <div className="user-name" style={{ fontSize: 13 }}>
                {user ? (user.attributes?.name || user.attributes?.email || user.username) : '—'}
              </div>
              <div className="user-role" style={{ fontSize: 11 }}>{roleLabel}</div>
            </div>
          )}
        </div>
      </div>

      {!isMobile && (
        <button
          data-testid="sidebar-collapse-btn"
          onClick={onToggleCollapse}
          className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center z-50 cursor-pointer text-text-muted hover:text-text-primary transition-colors"
          style={{ background: 'var(--navy-light)', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 1px 4px rgba(0,0,0,0.25)' }}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>
      )}

    </aside>
  );
}

function AppShell() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { alerts: headerAlerts, activeCount: headerAlertCount } = useAlerts();
  const [currentRole, setCurrentRole] = useState(() => localStorage.getItem('nn-current-role') || 'manager');
  const [isMobileViewport, setIsMobileViewport] = useState(() => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const sidebarWasCollapsed = useRef(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('nn-theme') || 'light');
  const menuButtonRef = useRef(null);
  const closeButtonRef = useRef(null);

  const handleChatOpenChange = useCallback((open) => {
    if (open) {
      setIsCollapsed(prev => {
        sidebarWasCollapsed.current = prev;
        return true;
      });
    } else {
      setIsCollapsed(sidebarWasCollapsed.current);
    }
    setIsChatOpen(open);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('nn-theme', theme);
  }, [theme]);

  // Persist role selection across refreshes
  useEffect(() => {
    localStorage.setItem('nn-current-role', currentRole);
  }, [currentRole]);

  // Sync role from Cognito user attribute on first login (no stored preference)
  useEffect(() => {
    if (user?.attributes?.['custom:role'] && !localStorage.getItem('nn-current-role')) {
      const role = user.attributes['custom:role'];
      setCurrentRole(role);
      const overviewPath =
        role === 'system-admin' ? '/system-admin/onboarding' :
        role === 'director' ? '/director/overview' :
        role === 'super-user' ? '/director/overview' : '/manager/overview';
      navigate(overviewPath, { replace: true });
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobileViewport(isMobile);
      if (isMobile) {
        setIsMobileSidebarOpen(false);
        setIsCollapsed(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsMobileSidebarOpen(false);
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    if (!isMobileSidebarOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (event.target.closest('[data-testid="app-sidebar"]')) return;
      setIsMobileSidebarOpen(false);
    };

    document.addEventListener('pointerdown', handleOutsideClick);
    return () => document.removeEventListener('pointerdown', handleOutsideClick);
  }, [isMobileSidebarOpen]);

  useEffect(() => {
    document.body.style.overflow = isMobileSidebarOpen ? 'hidden' : '';

    if (isMobileSidebarOpen) {
      closeButtonRef.current?.focus();
    } else if (isMobileViewport) {
      menuButtonRef.current?.focus();
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileSidebarOpen, isMobileViewport]);

  const PAGE_TITLE_MAP = {
    '/alerts': 'Operational Alerts',
    '/manager/overview': 'Practice Pulse', '/manager/priority-queue': 'Priority Queue',
    '/manager/quality': 'Quality Metrics', '/manager/quality-regulatory': 'Quality & Regulatory', '/manager/access': 'Access Metrics',
    '/manager/staffing': 'Staffing', '/manager/finance': 'Finance',
    '/manager/population-health': 'Population Health',
    '/manager/consumer-insights': 'Consumer Insights', '/manager/practice-management': 'Practice Management',
    '/manager/assignments': 'Assignments', '/manager/email-signal': 'Email Signals',
    '/favorites': 'Favorites',
    '/admin/checklists': 'Checklists', '/admin/roles': 'Role Management',
    '/admin/org': 'Organization', '/admin/data-sources': 'Data Sources',
    '/director/overview': 'Network Pulse', '/director/hierarchy': 'Team Hierarchy',
    '/director/access': 'Access', '/director/quality': 'Quality', '/director/quality-regulatory': 'Quality & Regulatory',
    '/director/staffing': 'Staffing', '/director/finance': 'Financial Tracking',
    '/director/initiatives': 'Initiatives', '/director/assignments': 'Assignments',
    '/director/email-signal': 'Email Signals', '/director/consumer-insights': 'Consumer Insights',
    '/director/population-health': 'Population Health',
    '/super-user/overview': 'Overview', '/super-user/dashboards': 'Dashboard Config',
    '/super-user/workflows': 'Workflows & Tasks', '/super-user/metrics': 'Metrics & Thresholds',
    '/super-user/alerts': 'Alerts & Escalations', '/super-user/features': 'Feature Management',
    '/super-user/training': 'Training & Support',
    '/system-admin/onboarding': 'Onboarding', '/system-admin/users': 'User Directory',
    '/system-admin/feature-flags': 'Feature Flags', '/system-admin/audit-log': 'Audit Log',
    '/system-admin/nsg': 'NSG Gateway',
    '/system-admin/demo-setup': 'Demo Setup',
    '/strategic-initiatives': 'Strategic Initiatives',
  };
  const pageTitle = PAGE_TITLE_MAP[location.pathname] || 'Clinical Operations Command Center';

  return (
    <ThemeProvider value={theme}>
    <ConsumerUnitProvider>
    <TooltipProvider delayDuration={0}>
    <ChatbotProvider>
    <ChatLayoutEffect onChange={handleChatOpenChange} />
    <div className="flex min-h-screen w-full bg-surface overflow-x-hidden">
      {!isMobileViewport && (
        <Sidebar
          currentRole={currentRole}
          userRole={user?.attributes?.['custom:role'] || 'manager'}
          onRoleChange={setCurrentRole}
          isMobile={false}
          isMobileOpen={false}
          onCloseMobile={() => setIsMobileSidebarOpen(false)}
          closeButtonRef={closeButtonRef}
          isCollapsed={isCollapsed}
          onToggleCollapse={() => setIsCollapsed(c => !c)}
          user={user}
          onSignOut={signOut}
          theme={theme}
          onThemeChange={setTheme}
          alertCount={headerAlertCount}
        />
      )}

      {isMobileViewport && isMobileSidebarOpen && (
        <>
          <div
            className="sidebar-backdrop"
            data-testid="sidebar-backdrop"
            onClick={() => setIsMobileSidebarOpen(false)}
            aria-hidden="true"
          />
          <Sidebar
            currentRole={currentRole}
            userRole={user?.attributes?.['custom:role'] || 'manager'}
            onRoleChange={setCurrentRole}
            isMobile={true}
            isMobileOpen={isMobileSidebarOpen}
            onCloseMobile={() => setIsMobileSidebarOpen(false)}
            closeButtonRef={closeButtonRef}
            user={user}
            onSignOut={signOut}
            theme={theme}
            onThemeChange={setTheme}
            alertCount={headerAlertCount}
          />
        </>
      )}

      <div
        className="flex-1 transition-all duration-300 app-main-region"
        style={{ marginLeft: isMobileViewport ? 0 : (isCollapsed ? COLLAPSED_SIDEBAR_WIDTH : DESKTOP_SIDEBAR_WIDTH), background: 'var(--surface)' }}
      >
        <Header
          pageTitle={pageTitle}
          showMenuButton={isMobileViewport}
          onMenuToggle={() => setIsMobileSidebarOpen((open) => !open)}
          menuButtonRef={menuButtonRef}
          notificationCount={headerAlertCount}
          headerAlerts={headerAlerts}
          currentRole={currentRole}
        />

        <main className="main-content" style={{ padding: 0, marginRight: (!isMobileViewport && isChatOpen) ? CHAT_PANEL_WIDTH : 0 }}>
          <Suspense fallback={<div className="dashboard-container loading-container"><div className="loading-spinner" /></div>}>
          <Routes>
              {currentRole === 'system-admin' ? (
                <>
                  {/* Dashboard (director paths) */}
                  <Route path="/admin/checklists" element={<ChecklistManagement />} />
                  <Route path="/admin/roles" element={<RoleManagement />} />
                  <Route path="/admin/org" element={<OrganizationHierarchyBuilder />} />
                  <Route path="/admin/data-sources" element={<DataOwnershipDirectory />} />
                  <Route path="/favorites" element={<FavoritesPage />} />
                  <Route path="/director/access" element={<DirectorAccessPage />} />
                  <Route path="/director/quality" element={<QualityTab />} />
                  <Route path="/director/quality-regulatory" element={<DirectorQualityRegulatoryPage />} />
                  <Route path="/director/staffing" element={<DirectorStaffingPage />} />
                  <Route path="/director/finance" element={<FinancialTrackingPage role="director" />} />
                  <Route path="/director/initiatives" element={<InitiativesTab />} />
                  <Route path="/director/assignments" element={<AssignmentsGate currentRole={currentRole}><AssignmentsView userId={user?.attributes?.sub || user?.username} userRole="director" /></AssignmentsGate>} />
                  <Route path="/director/email-signal" element={<EmailSignalGate currentRole={currentRole}><EmailSignalDashboard /></EmailSignalGate>} />
                  <Route path="/director/consumer-insights" element={<DirectorConsumerDashboard />} />
                  <Route path="/director/practice-management" element={<DirectorPracticeManagementDashboard />} />
                  <Route path="/director/population-health" element={<DirectorPopulationHealthPage />} />
                  <Route path="/director/overview" element={<React.Suspense fallback={<div className="p-6 text-sm text-text-muted">Loading...</div>}><NetworkPulsePage /></React.Suspense>} />
                  <Route path="/director/hierarchy" element={<DirectorView user={user} initialTab="hierarchy" />} />
                  <Route path="/strategic-initiatives" element={<StrategicInitiativesGate currentRole={currentRole}><StrategicInitiativesList /></StrategicInitiativesGate>} />
                  <Route path="/strategic-initiatives/new" element={<StrategicInitiativesGate currentRole={currentRole}><InitiativeForm /></StrategicInitiativesGate>} />
                  <Route path="/strategic-initiatives/:id" element={<StrategicInitiativesGate currentRole={currentRole}><InitiativeDetail /></StrategicInitiativesGate>} />
                  <Route path="/strategic-initiatives/:id/edit" element={<StrategicInitiativesGate currentRole={currentRole}><InitiativeForm /></StrategicInitiativesGate>} />
                  <Route path="/strategic-initiatives/:id/status-update" element={<StrategicInitiativesGate currentRole={currentRole}><StatusUpdateForm /></StrategicInitiativesGate>} />
                  {/* Configuration (super-user paths) */}
                  <Route path="/super-user/overview" element={<SuperUserDashboard />} />
                  <Route path="/super-user/dashboards" element={<SuperUserDashboard />} />
                  <Route path="/super-user/workflows" element={<SuperUserDashboard />} />
                  <Route path="/super-user/metrics" element={<SuperUserDashboard />} />
                  <Route path="/super-user/alerts" element={<SuperUserDashboard />} />
                  <Route path="/super-user/features" element={<SuperUserDashboard />} />
                  <Route path="/super-user/training" element={<SuperUserDashboard />} />
                  {/* NexoNurse Admin */}
                  <Route path="/system-admin/onboarding" element={<OnboardingWizard />} />
                  <Route path="/system-admin/users" element={<UserDirectory />} />
                  <Route path="/system-admin/feature-flags" element={<FeatureFlagsPage />} />
                  <Route path="/system-admin/audit-log" element={<AuditLogPage />} />
                  <Route path="/system-admin/nsg" element={<Suspense fallback={<div className="p-6 text-sm text-gray-400">Loading…</div>}><NsgAdminPage /></Suspense>} />
                  <Route path="/system-admin/demo-setup" element={<DemoSetupPage />} />
                  <Route path="/alerts" element={<AlertsPage />} />
                  <Route path="/" element={<Navigate to="/system-admin/onboarding" replace />} />
                </>
              ) : currentRole === 'super-user' ? (
                <>
                  {/* Dashboard (director paths) */}
                  <Route path="/admin/checklists" element={<ChecklistManagement />} />
                  <Route path="/admin/roles" element={<RoleManagement />} />
                  <Route path="/admin/org" element={<OrganizationHierarchyBuilder />} />
                  <Route path="/admin/data-sources" element={<DataOwnershipDirectory />} />
                  <Route path="/favorites" element={<FavoritesPage />} />
                  <Route path="/director/access" element={<DirectorAccessPage />} />
                  <Route path="/director/quality" element={<QualityTab />} />
                  <Route path="/director/quality-regulatory" element={<DirectorQualityRegulatoryPage />} />
                  <Route path="/director/staffing" element={<DirectorStaffingPage />} />
                  <Route path="/director/finance" element={<FinancialTrackingPage role="director" />} />
                  <Route path="/director/initiatives" element={<InitiativesTab />} />
                  <Route path="/director/assignments" element={<AssignmentsGate currentRole={currentRole}><AssignmentsView userId={user?.attributes?.sub || user?.username} userRole="director" /></AssignmentsGate>} />
                  <Route path="/director/email-signal" element={<EmailSignalGate currentRole={currentRole}><EmailSignalDashboard /></EmailSignalGate>} />
                  <Route path="/director/consumer-insights" element={<DirectorConsumerDashboard />} />
                  <Route path="/director/practice-management" element={<DirectorPracticeManagementDashboard />} />
                  <Route path="/director/population-health" element={<DirectorPopulationHealthPage />} />
                  <Route path="/director/overview" element={<React.Suspense fallback={<div className="p-6 text-sm text-text-muted">Loading...</div>}><NetworkPulsePage /></React.Suspense>} />
                  <Route path="/director/hierarchy" element={<DirectorView user={user} initialTab="hierarchy" />} />
                  <Route path="/strategic-initiatives" element={<StrategicInitiativesGate currentRole={currentRole}><StrategicInitiativesList /></StrategicInitiativesGate>} />
                  <Route path="/strategic-initiatives/new" element={<StrategicInitiativesGate currentRole={currentRole}><InitiativeForm /></StrategicInitiativesGate>} />
                  <Route path="/strategic-initiatives/:id" element={<StrategicInitiativesGate currentRole={currentRole}><InitiativeDetail /></StrategicInitiativesGate>} />
                  <Route path="/strategic-initiatives/:id/edit" element={<StrategicInitiativesGate currentRole={currentRole}><InitiativeForm /></StrategicInitiativesGate>} />
                  <Route path="/strategic-initiatives/:id/status-update" element={<StrategicInitiativesGate currentRole={currentRole}><StatusUpdateForm /></StrategicInitiativesGate>} />
                  {/* Configuration (super-user paths) */}
                  <Route path="/super-user/overview" element={<SuperUserDashboard />} />
                  <Route path="/super-user/dashboards" element={<SuperUserDashboard />} />
                  <Route path="/super-user/workflows" element={<SuperUserDashboard />} />
                  <Route path="/super-user/metrics" element={<SuperUserDashboard />} />
                  <Route path="/super-user/alerts" element={<SuperUserDashboard />} />
                  <Route path="/super-user/features" element={<SuperUserDashboard />} />
                  <Route path="/super-user/training" element={<SuperUserDashboard />} />
                  {/* system-admin can access NexoNurse Admin while impersonating super-user */}
                  <Route path="/system-admin/onboarding" element={<OnboardingWizard />} />
                  <Route path="/system-admin/users" element={<UserDirectory />} />
                  <Route path="/system-admin/feature-flags" element={<FeatureFlagsPage />} />
                  <Route path="/system-admin/audit-log" element={<AuditLogPage />} />
                  <Route path="/system-admin/demo-setup" element={<DemoSetupPage />} />
                  <Route path="/alerts" element={<AlertsPage />} />
                  <Route path="/" element={<Navigate to="/director/overview" replace />} />
                </>
              ) : currentRole === 'director' ? (
                <>
                  <Route path="/admin/checklists" element={<ChecklistManagement />} />
                  <Route path="/admin/roles" element={<RoleManagement />} />
                  <Route path="/admin/org" element={<OrganizationHierarchyBuilder />} />
                  <Route path="/admin/data-sources" element={<DataOwnershipDirectory />} />
                  <Route path="/favorites" element={<FavoritesPage />} />
                  <Route path="/initiatives" element={<InitiativesAssignmentsTab />} />
                  <Route path="/director/access" element={<DirectorAccessPage />} />
                  <Route path="/director/quality" element={<QualityTab />} />
                  <Route path="/director/quality-regulatory" element={<DirectorQualityRegulatoryPage />} />
                  <Route path="/director/staffing" element={<DirectorStaffingPage />} />
                  <Route path="/director/finance" element={<FinancialTrackingPage role="director" />} />
                  <Route path="/director/initiatives" element={<InitiativesTab />} />
                  <Route path="/director/assignments" element={<AssignmentsGate currentRole={currentRole}><AssignmentsView userId={user?.attributes?.sub || user?.username} userRole="director" /></AssignmentsGate>} />
                  <Route path="/director/email-signal" element={<EmailSignalGate currentRole={currentRole}><EmailSignalDashboard /></EmailSignalGate>} />
                  <Route path="/director/consumer-insights" element={<DirectorConsumerDashboard />} />
                  <Route path="/director/practice-management" element={<DirectorPracticeManagementDashboard />} />
                  <Route path="/director/population-health" element={<DirectorPopulationHealthPage />} />
                  <Route path="/director/overview" element={<React.Suspense fallback={<div className="p-6 text-sm text-text-muted">Loading...</div>}><NetworkPulsePage /></React.Suspense>} />
                  <Route path="/director/hierarchy" element={<DirectorView user={user} initialTab="hierarchy" />} />
                  <Route path="/strategic-initiatives" element={<StrategicInitiativesGate currentRole={currentRole}><StrategicInitiativesList /></StrategicInitiativesGate>} />
                  <Route path="/strategic-initiatives/new" element={<StrategicInitiativesGate currentRole={currentRole}><InitiativeForm /></StrategicInitiativesGate>} />
                  <Route path="/strategic-initiatives/:id" element={<StrategicInitiativesGate currentRole={currentRole}><InitiativeDetail /></StrategicInitiativesGate>} />
                  <Route path="/strategic-initiatives/:id/edit" element={<StrategicInitiativesGate currentRole={currentRole}><InitiativeForm /></StrategicInitiativesGate>} />
                  <Route path="/strategic-initiatives/:id/status-update" element={<StrategicInitiativesGate currentRole={currentRole}><StatusUpdateForm /></StrategicInitiativesGate>} />
                  <Route path="/system-admin/onboarding" element={<OnboardingWizard />} />
                  <Route path="/system-admin/users" element={<UserDirectory />} />
                  <Route path="/system-admin/feature-flags" element={<FeatureFlagsPage />} />
                  <Route path="/system-admin/audit-log" element={<AuditLogPage />} />
                  <Route path="/system-admin/demo-setup" element={<DemoSetupPage />} />
                  <Route path="/alerts" element={<AlertsPage />} />
                  <Route path="/" element={<Navigate to="/director/overview" replace />} />
                </>
              ) : (
                <>
                  <Route path="/manager/overview" element={<PracticePulsePage />} />
                  <Route path="/manager/priority-queue" element={<ManagerView user={user} initialTab="priority" />} />
                  <Route path="/manager/quality" element={<ManagerView user={user} initialTab="quality" />} />
                  <Route path="/manager/quality-regulatory" element={<QualityRegulatoryPage />} />
                  <Route path="/manager/access" element={<SchedulingAccessPage />} />
                  <Route path="/manager/staffing" element={<StaffingPage />} />
                  <Route path="/manager/finance" element={<FinancialTrackingPage role="manager" />} />
                  <Route path="/manager/consumer-insights" element={<ConsumerInsightsPage />} />
                  <Route path="/manager/practice-management" element={<PracticeManagementPage />} />
                  <Route path="/manager/population-health" element={<PopulationHealthPage />} />
                  <Route path="/manager/assignments" element={<AssignmentsGate currentRole={currentRole}><AssignmentsView userId={user?.attributes?.sub || user?.username} userRole="manager" /></AssignmentsGate>} />
                  <Route path="/manager/email-signal" element={<EmailSignalGate currentRole={currentRole}><EmailSignalDashboard /></EmailSignalGate>} />
                  <Route path="/manager/favorites" element={<FavoritesPage />} />
                  {/* system-admin can access NexoNurse Admin while impersonating manager */}
                  <Route path="/system-admin/onboarding" element={<OnboardingWizard />} />
                  <Route path="/system-admin/users" element={<UserDirectory />} />
                  <Route path="/system-admin/feature-flags" element={<FeatureFlagsPage />} />
                  <Route path="/system-admin/audit-log" element={<AuditLogPage />} />
                  <Route path="/system-admin/demo-setup" element={<DemoSetupPage />} />
                  <Route path="/alerts" element={<AlertsPage />} />
                  <Route path="/" element={<Navigate to="/manager/overview" replace />} />
                </>
              )}
            </Routes>
          </Suspense>
          </main>
        </div>
      </div>
      <ChatFAB />
      <ChatDrawer />
      <Toaster />
    </ChatbotProvider>
    </TooltipProvider>
    </ConsumerUnitProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public route — login page (no auth required) */}
          <Route path="/login" element={<LoginPage />} />

          {/* Protected routes — require authentication */}
          <Route element={<ProtectedRoute />}>
            <Route path="/*" element={<EventStreamProvider><AlertsProvider><AppShell /></AlertsProvider></EventStreamProvider>} />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
